/**
 * Excess rent reclaim
 *
 * Solana lowers the rent-exempt minimum in steps (SIMD-0437). Accounts created
 * before a step keep their old deposit, so every existing token account now holds
 * more SOL than it needs. The Token program (p-token) and Token-2022 both expose
 * `WithdrawExcessLamports` (instruction index 38), which moves lamports above the
 * current minimum out of a token account, mint or multisig without closing it and
 * without touching the token balance. Only the account owner has to sign.
 *
 * This module scans a wallet's token accounts, works out the excess per account
 * against the cluster's live rent parameters, and builds the batched transactions.
 * By default the lamports go straight back to the user's wallet. With
 * `creditsProgramId` set, each transaction ends with the Junk.fun program's
 * `deposit_or_withdraw`, which moves that batch's excess from the wallet into the
 * user's credits PDA (UserInfo) and credits its tracked balance, so the SOL lands
 * as chest credits. Sending the withdraw straight to the PDA would not work: the
 * program only counts lamports that arrive through `deposit_or_withdraw`.
 */

import {
  Connection,
  PublicKey,
  SystemProgram,
  Transaction,
  TransactionInstruction,
  ComputeBudgetProgram,
} from '@solana/web3.js';
import { TOKEN_PROGRAM_ID, TOKEN_2022_PROGRAM_ID } from '@solana/spl-token';

/** TokenInstruction::WithdrawExcessLamports, identical on Token and Token-2022 */
export const WITHDRAW_EXCESS_LAMPORTS_INSTRUCTION = 38;

/**
 * How many accounts fit in one legacy transaction. Each instruction adds one
 * unique 32-byte account key plus ~7 bytes; 20 leaves comfortable headroom under
 * the 1232-byte packet limit once the compute-budget instructions are included.
 * The optional credits deposit adds five keys and a 17-byte payload (~190 bytes)
 * and still fits at 20; the unit tests enforce both limits.
 */
export const MAX_ACCOUNTS_PER_TX = 20;

/** Priority fee used elsewhere in the app for user-signed transactions */
const PRIORITY_FEE_MICRO_LAMPORTS = 9000;
/** WithdrawExcessLamports is a few thousand CU; budget generously for a full batch */
const COMPUTE_UNIT_LIMIT = 200_000;
/** deposit_or_withdraw does init_if_needed, a system transfer and an event CPI */
const CREDITS_DEPOSIT_COMPUTE_UNITS = 100_000;

/**
 * Anchor discriminator of the Junk.fun program's `deposit_or_withdraw`:
 * sha256("global:deposit_or_withdraw")[0..8], as listed in idl/junk_fun_program.json.
 */
export const DEPOSIT_OR_WITHDRAW_DISCRIMINATOR = Buffer.from([223, 174, 227, 231, 188, 109, 69, 77]);

/** UserInfo PDA data: 8-byte discriminator + u64 balance + u8 bump */
export const CREDITS_ACCOUNT_SIZE = 17;

// SPL token account layout offsets (165-byte base, identical for Token-2022)
const TOKEN_ACCOUNT_BASE_SIZE = 165;
const OFFSET_MINT = 0;
const OFFSET_OWNER = 32;
const OFFSET_STATE = 108;
const OFFSET_IS_NATIVE_TAG = 109;
const ACCOUNT_STATE_INITIALIZED = 1;
const ACCOUNT_STATE_FROZEN = 2;

export interface ExcessRentAccount {
  /** Token account address */
  address: string;
  mint: string;
  programId: string;
  /** Current lamport balance */
  lamports: number;
  /** Rent-exempt minimum for this account's size under the live rent parameters */
  rentExemptMinimum: number;
  /** lamports - rentExemptMinimum */
  excessLamports: number;
  dataLength: number;
}

export interface ExcessRentScan {
  /** Accounts with reclaimable excess that we will include in the transactions */
  accounts: ExcessRentAccount[];
  /** Sum of excessLamports over `accounts` */
  totalExcessLamports: number;
  /** Token accounts scanned (both programs), including ones with no excess */
  scannedAccounts: number;
  /** Frozen accounts with excess that were skipped (the token program rejects them in a batch) */
  skippedFrozen: number;
  /** Wrapped-SOL accounts skipped (WithdrawExcessLamports does not support native accounts) */
  skippedNative: number;
  /** Rent-exempt minimum for a plain 165-byte token account, for display */
  tokenAccountMinimum: number;
}

/** Whether the user's Junk.fun credits PDA exists, and what creating it would cost */
export interface CreditsAccountStatus {
  /** UserInfo PDA address */
  address: string;
  exists: boolean;
  /** Lamports the wallet pays to create the PDA on a first deposit; 0 when it exists */
  rentLamports: number;
}

/** PDAs the Junk.fun `deposit_or_withdraw` instruction needs, for this owner */
function creditsAccounts(owner: PublicKey, programId: PublicKey) {
  const [globalConfig] = PublicKey.findProgramAddressSync([Buffer.from('global_config')], programId);
  const [userInfo] = PublicKey.findProgramAddressSync([Buffer.from('user_info'), owner.toBuffer()], programId);
  const [eventAuthority] = PublicKey.findProgramAddressSync([Buffer.from('__event_authority')], programId);
  return { globalConfig, userInfo, eventAuthority };
}

/** The owner's Junk.fun credits PDA (UserInfo) */
export function getCreditsAccountAddress(owner: PublicKey, programId: PublicKey): PublicKey {
  return creditsAccounts(owner, programId).userInfo;
}

/**
 * Check whether the owner's credits PDA exists. A first deposit creates it with
 * `init_if_needed`, and the wallet pays that rent on top of the deposit, so the
 * panel needs to know before offering to deposit a small reclaim.
 */
export async function getCreditsAccountStatus(
  connection: Connection,
  owner: PublicKey,
  programId: PublicKey
): Promise<CreditsAccountStatus> {
  const address = getCreditsAccountAddress(owner, programId);
  const info = await connection.getAccountInfo(address);
  if (info) {
    return { address: address.toBase58(), exists: true, rentLamports: 0 };
  }
  const rentLamports = await connection.getMinimumBalanceForRentExemption(CREDITS_ACCOUNT_SIZE);
  return { address: address.toBase58(), exists: false, rentLamports };
}

/**
 * Build the Junk.fun `deposit_or_withdraw(is_deposit = true, amount)` instruction:
 * moves `amountLamports` from the owner's wallet into their credits PDA and
 * credits the tracked balance. Accounts follow the IDL order.
 */
export function createDepositToCreditsInstruction(
  owner: PublicKey,
  amountLamports: number,
  programId: PublicKey
): TransactionInstruction {
  if (!Number.isSafeInteger(amountLamports) || amountLamports <= 0) {
    throw new Error(`Invalid credits deposit amount: ${amountLamports}`);
  }
  const { globalConfig, userInfo, eventAuthority } = creditsAccounts(owner, programId);

  const data = Buffer.alloc(DEPOSIT_OR_WITHDRAW_DISCRIMINATOR.length + 1 + 8);
  DEPOSIT_OR_WITHDRAW_DISCRIMINATOR.copy(data, 0);
  data.writeUInt8(1, DEPOSIT_OR_WITHDRAW_DISCRIMINATOR.length); // is_deposit = true
  data.writeBigUInt64LE(BigInt(amountLamports), DEPOSIT_OR_WITHDRAW_DISCRIMINATOR.length + 1);

  return new TransactionInstruction({
    programId,
    keys: [
      { pubkey: owner, isSigner: true, isWritable: true },
      { pubkey: globalConfig, isSigner: false, isWritable: false },
      { pubkey: userInfo, isSigner: false, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: eventAuthority, isSigner: false, isWritable: false },
      { pubkey: programId, isSigner: false, isWritable: false },
    ],
    data,
  });
}

/**
 * Build a `WithdrawExcessLamports` instruction. The owner (or delegate) signs;
 * the destination can be any account.
 */
export function createWithdrawExcessLamportsInstruction(
  source: PublicKey,
  destination: PublicKey,
  owner: PublicKey,
  programId: PublicKey
): TransactionInstruction {
  return new TransactionInstruction({
    programId,
    keys: [
      { pubkey: source, isSigner: false, isWritable: true },
      { pubkey: destination, isSigner: false, isWritable: true },
      { pubkey: owner, isSigner: true, isWritable: false },
    ],
    data: Buffer.from([WITHDRAW_EXCESS_LAMPORTS_INSTRUCTION]),
  });
}

/**
 * Scan every token account the wallet owns (Token and Token-2022) and compute the
 * excess above the live rent-exempt minimum for each one.
 */
export async function scanExcessRent(
  connection: Connection,
  owner: PublicKey
): Promise<ExcessRentScan> {
  const [legacy, token2022, tokenAccountMinimum] = await Promise.all([
    connection.getTokenAccountsByOwner(owner, { programId: TOKEN_PROGRAM_ID }),
    connection.getTokenAccountsByOwner(owner, { programId: TOKEN_2022_PROGRAM_ID }),
    connection.getMinimumBalanceForRentExemption(TOKEN_ACCOUNT_BASE_SIZE),
  ]);

  const raw = [...legacy.value, ...token2022.value];

  // Rent minimum depends only on data length; Token-2022 accounts vary by extensions
  const minimumBySize = new Map<number, number>([[TOKEN_ACCOUNT_BASE_SIZE, tokenAccountMinimum]]);
  const sizes = Array.from(new Set(raw.map((a) => a.account.data.length))).filter(
    (size) => !minimumBySize.has(size)
  );
  const minimums = await Promise.all(sizes.map((size) => connection.getMinimumBalanceForRentExemption(size)));
  sizes.forEach((size, i) => minimumBySize.set(size, minimums[i]));

  const accounts: ExcessRentAccount[] = [];
  let skippedFrozen = 0;
  let skippedNative = 0;

  for (const { pubkey, account } of raw) {
    const data = account.data;
    if (data.length < TOKEN_ACCOUNT_BASE_SIZE) continue;

    // Only accounts this wallet actually owns can be drained by its signature
    const accountOwner = new PublicKey(data.subarray(OFFSET_OWNER, OFFSET_OWNER + 32));
    if (!accountOwner.equals(owner)) continue;

    const rentExemptMinimum = minimumBySize.get(data.length) ?? tokenAccountMinimum;
    const excessLamports = account.lamports - rentExemptMinimum;
    if (excessLamports <= 0) continue;

    const state = data[OFFSET_STATE];
    const isNative = data.readUInt32LE(OFFSET_IS_NATIVE_TAG) === 1;

    if (isNative) {
      // Wrapped SOL: the "excess" is the user's wSOL balance, and the token program
      // refuses WithdrawExcessLamports on native accounts anyway.
      skippedNative++;
      continue;
    }
    if (state === ACCOUNT_STATE_FROZEN) {
      skippedFrozen++;
      continue;
    }
    if (state !== ACCOUNT_STATE_INITIALIZED) continue;

    accounts.push({
      address: pubkey.toBase58(),
      mint: new PublicKey(data.subarray(OFFSET_MINT, OFFSET_MINT + 32)).toBase58(),
      programId: account.owner.toBase58(),
      lamports: account.lamports,
      rentExemptMinimum,
      excessLamports,
      dataLength: data.length,
    });
  }

  // Largest excess first so a partial run still returns the most value
  accounts.sort((a, b) => b.excessLamports - a.excessLamports);

  return {
    accounts,
    totalExcessLamports: accounts.reduce((sum, a) => sum + a.excessLamports, 0),
    scannedAccounts: raw.length,
    skippedFrozen,
    skippedNative,
    tokenAccountMinimum,
  };
}

/**
 * Group accounts into per-transaction batches. Grouped by token program so a
 * failure in one program's instruction can never sink a batch that also carried
 * the other program's accounts. Within a group the scan order (largest excess
 * first) is preserved.
 */
function batchByProgram<T extends Pick<ExcessRentAccount, 'programId'>>(accounts: T[], maxPerTx: number): T[][] {
  const grouped = [...accounts].sort((a, b) => a.programId.localeCompare(b.programId));
  const batches: T[][] = [];
  for (const account of grouped) {
    const current = batches[batches.length - 1];
    if (current && current.length < maxPerTx && current[0].programId === account.programId) {
      current.push(account);
    } else {
      batches.push([account]);
    }
  }
  return batches;
}

/** How many transactions `buildWithdrawExcessTransactions` will produce for these accounts */
export function countWithdrawBatches(
  accounts: Pick<ExcessRentAccount, 'programId'>[],
  maxPerTx: number = MAX_ACCOUNTS_PER_TX
): number {
  return batchByProgram(accounts, maxPerTx).length;
}

/** The accounts carried by each transaction, in the order `buildWithdrawExcessTransactions` sends them */
export function groupWithdrawBatches(
  accounts: ExcessRentAccount[],
  maxPerTx: number = MAX_ACCOUNTS_PER_TX
): ExcessRentAccount[][] {
  return batchByProgram(accounts, maxPerTx);
}

/** The same scan without the given accounts, totals recomputed. Used once a batch has landed. */
export function withoutAccounts(scan: ExcessRentScan, addresses: ReadonlySet<string>): ExcessRentScan {
  if (addresses.size === 0) return scan;
  const accounts = scan.accounts.filter((a) => !addresses.has(a.address));
  if (accounts.length === scan.accounts.length) return scan;
  return {
    ...scan,
    accounts,
    totalExcessLamports: accounts.reduce((sum, a) => sum + a.excessLamports, 0),
  };
}

export interface BuildWithdrawExcessOptions {
  maxPerTx?: number;
  /**
   * Junk.fun program id. When set, every transaction ends with a `deposit_or_withdraw`
   * that moves the batch's excess from the wallet into the owner's credits PDA. The
   * withdraws and the deposit share one transaction, so they land or fail together.
   */
  creditsProgramId?: PublicKey;
}

/**
 * Build the batched legacy transactions that withdraw the excess from every
 * account to the owner's wallet, optionally depositing it into Junk.fun credits
 * in the same transaction. Fee payer and blockhash are set by the caller.
 */
export function buildWithdrawExcessTransactions(
  accounts: ExcessRentAccount[],
  owner: PublicKey,
  options: BuildWithdrawExcessOptions = {}
): Transaction[] {
  const { maxPerTx = MAX_ACCOUNTS_PER_TX, creditsProgramId } = options;
  const computeUnits = COMPUTE_UNIT_LIMIT + (creditsProgramId ? CREDITS_DEPOSIT_COMPUTE_UNITS : 0);
  const transactions: Transaction[] = [];

  for (const batch of batchByProgram(accounts, maxPerTx)) {
    const tx = new Transaction();
    tx.add(
      ComputeBudgetProgram.setComputeUnitLimit({ units: computeUnits }),
      ComputeBudgetProgram.setComputeUnitPrice({ microLamports: PRIORITY_FEE_MICRO_LAMPORTS })
    );
    for (const account of batch) {
      tx.add(
        createWithdrawExcessLamportsInstruction(
          new PublicKey(account.address),
          owner,
          owner,
          new PublicKey(account.programId)
        )
      );
    }
    if (creditsProgramId) {
      // The withdraws above have just put exactly this much into the wallet
      const batchExcess = batch.reduce((sum, a) => sum + a.excessLamports, 0);
      if (batchExcess > 0) {
        tx.add(createDepositToCreditsInstruction(owner, batchExcess, creditsProgramId));
      }
    }
    transactions.push(tx);
  }

  return transactions;
}
